import isEqual from "lodash-es/isEqual";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import * as z from "zod";

import {
  canvasFrameSchema,
  canvasItemSchema,
  canvasSetSchema,
  sortFramesByGrid,
} from "./document";
import { getCanvasIdCollisionError } from "./frameValidation";

type CanvasSet = z.infer<typeof canvasSetSchema>;
type CanvasFrame = z.infer<typeof canvasFrameSchema>;
type CanvasItem = z.infer<typeof canvasItemSchema>;

/**
 * The largest set the coordinator will hold. Frames are handles rather than
 * bytes, so a real set is tens of kilobytes; the cap
 * keeps a single document comfortably under Durable Object storage's value limit.
 */
export const CANVAS_SET_MAX_BYTES = 1024 * 1024;

export const CANVAS_OPERATIONS_MAX = 256;

/**
 * One change to a set, at frame granularity. The coordinator applies operations
 * in server order and every editor applies the same function to the same base,
 * so two people editing different frames never conflict; the same frame is
 * last-writer-wins.
 */
export const canvasOperationSchema = z
  .discriminatedUnion("kind", [
    /** A frame's place is its own `row`/`col`, so a put carries no position. */
    z.strictObject({ kind: z.literal("putFrame"), frame: canvasFrameSchema }),
    z.strictObject({
      kind: z.literal("deleteFrame"),
      frameId: z.string().min(1),
    }),
    z.strictObject({
      kind: z.literal("putItem"),
      item: canvasItemSchema,
      /** Where a new item goes; an existing item keeps its position. */
      index: z.number().int().nonnegative(),
    }),
    z.strictObject({
      kind: z.literal("deleteItem"),
      itemId: z.string().min(1),
    }),
    z.strictObject({
      kind: z.literal("setCoverFrame"),
      coverFrameId: z.string().min(1).nullable(),
    }),
  ])
  .describe("CanvasOperation");

export type CanvasOperation = z.infer<typeof canvasOperationSchema>;

export type CanvasApplyOutcome =
  | { ok: true; set: CanvasSet }
  | { ok: false; error: string };

/** Drop edges and section members that point at something gone, and sections left empty, so deleting a frame or a step never fails because something else still names it. */
export function pruneCanvasItems({
  frames,
  items,
}: {
  frames: CanvasFrame[];
  items: CanvasItem[];
}): CanvasItem[] {
  const steps = new Map(
    items.flatMap((item) => (item.kind === "step" ? [[item.id, item]] : [])),
  );
  const memberIds = new Set([
    ...frames.map((frame) => frame.id),
    ...steps.keys(),
  ]);
  let changed = false;
  const kept: CanvasItem[] = [];
  const frameIds = new Set(frames.map((frame) => frame.id));
  for (const item of items) {
    if (item.kind === "edge") {
      const from = steps.get(item.from);
      // An ad's call to action opens the first page: an arrow may leave an ad frame, whole.
      const fromAd = frameIds.has(item.from) && item.fromElement === undefined;
      // An arrow leaves a step, or an element of its HTML that carries the id.
      const leavesSomething =
        item.fromElement === undefined ||
        Boolean(from?.html.includes(`data-id="${item.fromElement}"`));
      const to = steps.get(item.to);
      // An inspiration card is a look to follow, not a screen, so nothing leads into or out of it.
      const joinsScreens =
        from?.role !== "inspiration" && to?.role !== "inspiration";
      if (
        (!from && !fromAd) ||
        !to ||
        item.from === item.to ||
        (!fromAd && !leavesSomething) ||
        !joinsScreens
      ) {
        changed = true;
        continue;
      }
    }
    if (item.kind === "section") {
      const members = item.members.filter((id) => memberIds.has(id));
      if (members.length === 0 || members.length !== item.members.length) {
        changed = true;
        // A section with nobody left has no rectangle, so nothing could select or delete it.
        if (members.length > 0) {
          kept.push({ ...item, members });
        }
        continue;
      }
    }
    kept.push(item);
  }
  return changed ? kept : items;
}

/** Apply operations to a set. Pure: the input set is never mutated. */
export function applyCanvasOperations({
  set,
  operations,
}: {
  set: CanvasSet;
  operations: CanvasOperation[];
}): CanvasApplyOutcome {
  let next: CanvasSet = { ...set, frames: [...set.frames] };
  let items = [...set.items];
  for (const operation of operations) {
    exhaustiveSwitch(
      operation,
      "kind",
    )<void>({
      putFrame: ({ frame }) => {
        const index = next.frames.findIndex((entry) => entry.id === frame.id);
        if (index === -1) {
          next.frames.push(frame);
        } else {
          next.frames[index] = frame;
        }
      },
      deleteFrame: ({ frameId }) => {
        next.frames = next.frames.filter((entry) => entry.id !== frameId);
      },
      putItem: ({ item, index: position }) => {
        const index = items.findIndex((entry) => entry.id === item.id);
        if (index === -1) {
          items.splice(Math.min(position, items.length), 0, item);
        } else {
          items[index] = item;
        }
      },
      deleteItem: ({ itemId }) => {
        items = items.filter((entry) => entry.id !== itemId);
      },
      setCoverFrame: ({ coverFrameId }) => {
        next = { ...next, coverFrameId };
      },
    });
  }
  if (
    next.coverFrameId !== null &&
    !next.frames.some((frame) => frame.id === next.coverFrameId)
  ) {
    next = { ...next, coverFrameId: null };
  }
  items = pruneCanvasItems({ frames: next.frames, items });
  const collision = getCanvasIdCollisionError({ frames: next.frames, items });
  if (collision) {
    return { ok: false, error: collision };
  }
  const parsed = canvasSetSchema.safeParse({
    ...next,
    frames: sortFramesByGrid(next.frames),
    items,
  });
  if (!parsed.success) {
    return { ok: false, error: "The result is not a valid Canvas set." };
  }
  return { ok: true, set: parsed.data };
}

// Key order must not count: zod rebuilds objects in schema order, so a saved copy never matches the editor's own literal byte for byte.
export function sameJson(
  left: CanvasFrame | CanvasItem,
  right: typeof left,
): boolean {
  if (left === right) {
    return true;
  }
  return isEqual(
    JSON.parse(JSON.stringify(left)),
    JSON.parse(JSON.stringify(right)),
  );
}

/** The operations that turn `before` into `after`; empty when they already match. */
export function diffCanvasSets({
  before,
  after,
}: {
  before: CanvasSet;
  after: CanvasSet;
}): CanvasOperation[] {
  const operations: CanvasOperation[] = [];
  const previous = new Map(before.frames.map((frame) => [frame.id, frame]));
  const remaining = new Set(previous.keys());
  for (const frame of after.frames) {
    remaining.delete(frame.id);
    const existing = previous.get(frame.id);
    if (!existing || !sameJson(existing, frame)) {
      operations.push({ kind: "putFrame", frame });
    }
  }
  for (const frameId of remaining) {
    operations.push({ kind: "deleteFrame", frameId });
  }
  const previousItems = new Map(before.items.map((item) => [item.id, item]));
  const remainingItems = new Set(previousItems.keys());
  after.items.forEach((item, index) => {
    remainingItems.delete(item.id);
    const existing = previousItems.get(item.id);
    if (!existing || !sameJson(existing, item)) {
      operations.push({ kind: "putItem", item, index });
    }
  });
  for (const itemId of remainingItems) {
    operations.push({ kind: "deleteItem", itemId });
  }
  if (before.coverFrameId !== after.coverFrameId) {
    operations.push({
      kind: "setCoverFrame",
      coverFrameId: after.coverFrameId,
    });
  }
  return operations;
}

/** Frame ids a batch writes or removes. */
export function touchedFrameIds(operations: CanvasOperation[]): Set<string> {
  const ids = new Set<string>();
  for (const operation of operations) {
    if (operation.kind === "putFrame") {
      ids.add(operation.frame.id);
    }
    if (operation.kind === "deleteFrame") {
      ids.add(operation.frameId);
    }
  }
  return ids;
}

/** Item ids a batch writes or removes. */
export function touchedItemIds(operations: CanvasOperation[]): Set<string> {
  const ids = new Set<string>();
  for (const operation of operations) {
    if (operation.kind === "putItem") {
      ids.add(operation.item.id);
    }
    if (operation.kind === "deleteItem") {
      ids.add(operation.itemId);
    }
  }
  return ids;
}
